import { useMemo, useState } from "react";
import { LocalDate } from "@js-joda/core";
import { CircularProgress } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartCard,
  ChartHeader,
  ChartControls,
  ChartTitle,
  LoaderWrapper,
} from "./analytics.chart.styles";
import { SegmentController } from "components/designV2/segmentcontroller/segmentcontroller";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { ColorV2 } from "config/colors";
import { FontFamily } from "config/font";
import { type Order } from "model/Analytics";
import { type TimeReportDto } from "model/TimeReport";
import {
  type AnalyticsPeriod,
  type OperatingHours,
} from "./analytics.operatingHours";
import { type CustomCost } from "./analytics.customCosts";
import { type CustomSale } from "./analytics.customSales";
import { type CompanyTipTotal } from "./analytics.companyTips";
import { buildChartData } from "./analytics.chart.logic";
import { getShortMonthLabels } from "web-apps/company/utils/utils";

// ─── Props ────────────────────────────────────────────────────────────────────

interface AnalyticsChartProps {
  orders: Order[];
  timeReports: TimeReportDto[];
  period: AnalyticsPeriod;
  selectedDate: LocalDate;
  operatingHours: OperatingHours;
  /** Actual total from the API — chart buckets are scaled so they sum to this value exactly */
  totalPersonnelCost: number | null;
  /** Custom costs — period costs distributed evenly; date costs attributed to their specific bucket */
  customCosts: CustomCost[];
  /** Custom sales — each attributed to its specific date bucket's sales figure */
  customSales: CustomSale[];
  /** Tips recorded on the Tips page — folded into each date bucket's tips */
  companyTips: CompanyTipTotal[];
  loading: boolean;
  onEditHours: () => void;
}

// ─── Presentation constants ───────────────────────────────────────────────────

const AXIS_TICK_STYLE = {
  fill: ColorV2.Blue4,
  fontSize: 12,
  fontFamily: FontFamily.SpaceGroteskRegular,
};

const TOOLTIP_PROPS = {
  formatter: (
    value: number | string | (number | string)[],
    name: string | number,
  ) => [
    typeof value === "number"
      ? `${Math.round(value).toLocaleString("sv-SE")} kr`
      : String(value),
    name,
  ],
  contentStyle: {
    background: ColorV2.White,
    border: `1.5px solid ${ColorV2.Blue5}`,
    borderRadius: "12px",
    fontSize: "13px",
    fontFamily: FontFamily.SpaceGroteskRegular,
  },
};

const LEGEND_PROPS = {
  wrapperStyle: {
    fontSize: "13px",
    color: ColorV2.Blue3,
    fontFamily: FontFamily.SpaceGroteskRegular,
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

type ChartType = "line" | "bar";

export const AnalyticsChart = (props: AnalyticsChartProps) => {
  const { t, i18n } = useTranslation();
  const [chartType, setChartType] = useState<ChartType>("bar");

  // Stable references (recomputed only when the language actually changes)
  // so they don't defeat the `data` memoization below on every render.
  const weekdayLabels = useMemo(
    () => [
      t("WeekDays.MONDAYSHORT"),
      t("WeekDays.TUESDAYSHORT"),
      t("WeekDays.WEDNESDAYSHORT"),
      t("WeekDays.THURSDAYSHORT"),
      t("WeekDays.FRIDAYSHORT"),
      t("WeekDays.SATURDAYSHORT"),
      t("WeekDays.SUNDAYSHORT"),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language],
  );

  const monthLabels = useMemo(
    () => getShortMonthLabels(i18n.language),
    [i18n.language],
  );

  // "sales" and "customSales" share a stackId so their bars combine into one
  // column matching total sales height — customSales rendered in a lighter
  // tint of the same blue so it reads as "part of sales" rather than a
  // wholly separate metric. personnelCost/otherCosts pair up the same way,
  // in a lighter red, stacked into one "costs" column.
  const series: {
    dataKey: string;
    name: string;
    color: string;
    stackId?: string;
  }[] = [
    {
      dataKey: "sales",
      name: t("CompanyAnalytics.Sales"),
      color: ColorV2.ElectricBlue,
      stackId: "sales",
    },
    {
      dataKey: "customSales",
      name: t("CompanyAnalytics.CustomSales"),
      color: `${ColorV2.ElectricBlue}80`,
      stackId: "sales",
    },
    {
      dataKey: "personnelCost",
      name: t("CompanyAnalytics.PersonnelCost"),
      color: ColorV2.NegativeRed,
      stackId: "costs",
    },
    {
      dataKey: "otherCosts",
      name: t("CompanyAnalytics.CustomCosts"),
      color: `${ColorV2.NegativeRed}80`,
      stackId: "costs",
    },
    {
      dataKey: "tips",
      name: t("CompanyAnalytics.Tips"),
      color: ColorV2.PositiveGreen,
    },
  ];

  const chartTypeOptions = [
    { value: "bar", label: t("CompanyAnalytics.BarChart") },
    { value: "line", label: t("CompanyAnalytics.LineChart") },
  ];

  // Full bucket construction over orders/time-reports/customCosts/
  // customSales/companyTips — memoized so it doesn't re-run on every
  // re-render of the parent Analytics component (e.g. drag-reordering a
  // stat card updates unrelated state dozens of times per second while
  // dragging).
  const data = useMemo(
    () =>
      buildChartData({
        orders: props.orders,
        timeReports: props.timeReports,
        period: props.period,
        selectedDate: props.selectedDate,
        operatingHours: props.operatingHours,
        weekdayLabels,
        monthLabels,
        totalPersonnelCost: props.totalPersonnelCost,
        customCosts: props.customCosts,
        customSales: props.customSales,
        companyTips: props.companyTips,
      }),
    [
      props.orders,
      props.timeReports,
      props.period,
      props.selectedDate,
      props.operatingHours,
      weekdayLabels,
      monthLabels,
      props.totalPersonnelCost,
      props.customCosts,
      props.customSales,
      props.companyTips,
    ],
  );

  const commonProps = {
    data,
    margin: { top: 8, right: 16, left: 16, bottom: 4 },
  };

  const commonChildren = (
    <>
      <CartesianGrid
        strokeDasharray="3 3"
        stroke={ColorV2.Blue6}
        vertical={false}
      />
      <XAxis
        dataKey="label"
        tick={AXIS_TICK_STYLE}
        tickLine={false}
        axisLine={{ stroke: ColorV2.Blue5 }}
      />
      <YAxis
        tick={AXIS_TICK_STYLE}
        tickLine={false}
        axisLine={false}
        tickFormatter={(v: number) =>
          v >= 1000 ? `${Math.round(v / 1000)}k` : String(Math.round(v))
        }
      />
      <Tooltip {...TOOLTIP_PROPS} />
      <Legend {...LEGEND_PROPS} />
    </>
  );

  return (
    <ChartCard>
      <ChartHeader>
        <ChartTitle>{t("CompanyAnalytics.SalesAndCosts")}</ChartTitle>
        <ChartControls>
          <SegmentController
            options={chartTypeOptions}
            value={chartType}
            onChange={(v) => {
              if (v === "bar" || v === "line") setChartType(v);
            }}
          />
          <SecondaryButton
            label={t("CompanyAnalytics.ChangeHours")}
            size="small"
            onClick={props.onEditHours}
          />
        </ChartControls>
      </ChartHeader>
      {props.loading ? (
        <LoaderWrapper>
          <CircularProgress size={32} />
        </LoaderWrapper>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          {chartType === "line" ? (
            <LineChart {...commonProps}>
              {commonChildren}
              {series.map((s) => (
                <Line
                  key={s.dataKey}
                  type="monotone"
                  dataKey={s.dataKey}
                  name={s.name}
                  stroke={s.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              ))}
            </LineChart>
          ) : (
            <ComposedChart {...commonProps} barCategoryGap="15%" barGap={2}>
              {commonChildren}
              {series.map((s) => (
                <Bar
                  key={s.dataKey}
                  dataKey={s.dataKey}
                  name={s.name}
                  fill={s.color}
                  stackId={s.stackId}
                  radius={[3, 3, 0, 0]}
                />
              ))}
              <Line
                type="monotone"
                dataKey="result"
                name={t("CompanyAnalytics.Result")}
                stroke={ColorV2.Blue1}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </ComposedChart>
          )}
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
};
